import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type StartRequest =
  paths["/competitor-link-removals"]["post"]["requestBody"]["content"]["application/json"];

type StartResponse =
  paths["/competitor-link-removals"]["post"]["responses"]["201"]["content"]["application/json"];

// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения
// и список полей, не прошедших проверку.
interface ErrorBody {
  // Отсутствует, когда бэкенд ответил ошибкой без разобранного тела: сообщение
  // тогда собирается из кода ответа.
  error?: string;
  // Отсутствует, когда отказ не связан с полями формы: проверять нечего.
  fields?: unknown;
}

export type FieldErrors = Partial<Record<string, string>>;

// The backend returns validation failures as { error, fields[] } without per-field
// messages, so the frontend owns the wording (frontend.interface-copy). Unknown
// field keys fall back to a generic Russian message.
const FIELD_ERROR_MESSAGES: Record<string, string> = {
  competitorUrls: "Укажите хотя бы один корректный URL конкурента (по одному в строке)",
};

// autoRemove default: OFF. The default flow is scan-first — the run stops in
// `awaiting_confirmation` so the operator reviews found posts before deletion
// (CLR-03 «Подтвердить удаление»). Auto-removal is an explicit opt-in.
const DEFAULT_AUTO_REMOVE = false;

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string | null; fieldErrors: FieldErrors };

export interface UseCompetitorLinkRemovalStartResult {
  competitorUrlsText: string;
  autoRemove: boolean;
  submitState: SubmitState;
  setCompetitorUrlsText: (value: string) => void;
  setAutoRemove: (value: boolean) => void;
  submit: () => void;
}

export function parseCompetitorUrls(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function mapFieldErrors(fields: unknown): FieldErrors {
  let fieldErrorsOutcome: FieldErrors;
  const fieldErrors: FieldErrors = {};
  if (!Array.isArray(fields)) {
    fieldErrorsOutcome = fieldErrors;
  } else {
    for (const field of fields) {
      if (typeof field !== "string") {
        continue;
      }
      fieldErrors[field] = FIELD_ERROR_MESSAGES[field] ?? "Некорректное значение";
    }
    fieldErrorsOutcome = fieldErrors;
  }
  return fieldErrorsOutcome;
}

export function useCompetitorLinkRemovalStart(): UseCompetitorLinkRemovalStartResult {
  const navigate = useNavigate();
  const [competitorUrlsText, setCompetitorUrlsText] = useState("");
  const [autoRemove, setAutoRemove] = useState(DEFAULT_AUTO_REMOVE);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" });

  const submit = useCallback(() => {
    void (async () => {
      const competitorUrls = parseCompetitorUrls(competitorUrlsText);
      if (competitorUrls.length === 0) {
        setSubmitState({
          kind: "error",
          message: null,
          fieldErrors: { competitorUrls: FIELD_ERROR_MESSAGES.competitorUrls },
        });
        return;
      }

      setSubmitState({ kind: "submitting" });

      try {
        const body: StartRequest = { competitorUrls, autoRemove };
        const response = await fetchWithRefresh("/api/competitor-link-removals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorBody = (await response.json()) as ErrorBody;
          const fieldErrors = mapFieldErrors(errorBody.fields);
          const hasFieldErrors = Object.keys(fieldErrors).length > 0;
          setSubmitState({
            kind: "error",
            message: hasFieldErrors ? null : "Не удалось запустить очистку. Проверьте активные сайты и прокси или дождитесь завершения текущего прогона.",
            fieldErrors,
          });
          return;
        }

        const data = (await response.json()) as StartResponse;
        void navigate(`/competitor-link-removals/${data.removalId}/show`);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to start competitor link removal");
        setSubmitState({ kind: "error", message: "Ошибка сети. Попробуйте ещё раз.", fieldErrors: {} });
      }
    })();
  }, [competitorUrlsText, autoRemove, navigate]);

  return {
    competitorUrlsText,
    autoRemove,
    submitState,
    setCompetitorUrlsText,
    setAutoRemove,
    submit,
  };
}
